import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";
import Rails from "@rails/ujs";
import DropdownComponentController from "../dropdown_component_controller";
import { searchPattern } from "./search_pattern";

const WRAPPER = '[data-controller~="dropdown-component"]';

// Filters a dropdown panel's results. Needs an `input` target with
// data-action="input->dropdown-component--search#filter
// keydown->dropdown-component--search#navigate", and an `item` target with
// data-search="<text>" on each result.

export default class extends Controller {
  static targets = ["input", "item"];

  connect() {
    this.dropdown = this.element.closest(WRAPPER);
    this.onShown = (event) => {
      if (event.target === this.dropdown) this.inputTarget.focus();
    };
    this.onHidden = (event) => {
      if (event.target === this.dropdown) this.reset();
    };
    if (this.dropdown) {
      this.dropdown.addEventListener("dropdown-component:shown", this.onShown);
      this.dropdown.addEventListener(
        "dropdown-component:hidden",
        this.onHidden,
      );
    }
  }

  disconnect() {
    if (this.dropdown) {
      this.dropdown.removeEventListener(
        "dropdown-component:shown",
        this.onShown,
      );
      this.dropdown.removeEventListener(
        "dropdown-component:hidden",
        this.onHidden,
      );
    }
    this.dropdown = null;
  }

  filter() {
    const value = this.inputTarget.value;
    const pattern = searchPattern(value);
    this.itemTargets.forEach((item) => {
      const text = item.getAttribute("data-search");
      const match = value === "" || (text !== null && pattern.test(text));
      item.classList.toggle("d-none", !match);
      item.classList.remove("active");
    });
    this.visibleItems()[0]?.classList.add("active");
  }

  navigate(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        event.stopPropagation();
        this.move(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        event.stopPropagation();
        this.move(-1);
        break;
      case "Enter":
        if (event.isComposing) return;
        event.preventDefault();
        this.activate();
        break;
    }
  }

  move(step) {
    const visible = this.visibleItems();
    const index = visible.findIndex((el) => el.classList.contains("active"));
    if (index === -1) {
      if (step > 0) visible[0]?.classList.add("active");
      return;
    }
    const next = visible[index + step];
    if (next) {
      visible[index].classList.remove("active");
      next.classList.add("active");
      next.scrollIntoView?.({ block: "nearest" });
    }
  }

  activate() {
    const results = this.visibleItems().filter((el) =>
      el.classList.contains("active"),
    );
    if (results.length !== 1) return;
    const el = results[0];
    if (el.hasAttribute("data-remote") || el.hasAttribute("data-click")) {
      Rails.fire(el, "click");
    } else {
      DropdownComponentController.for(this.element)?.hide();
      Turbo.visit(el.href);
    }
  }

  reset() {
    this.inputTarget.value = "";
    this.inputTarget.blur();
    this.itemTargets.forEach((item) =>
      item.classList.remove("d-none", "active"),
    );
  }

  visibleItems() {
    return this.itemTargets.filter((el) => !el.classList.contains("d-none"));
  }
}
